import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Loader2, XCircle } from "lucide-react";
import FooterSection from "../components/FooterSection";
import { useSeo } from "../lib/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ThankYouPage() {
  useSeo({
    title: "Your Kill Switch Is Ready | Debt Collector Kill Switch",
    description: "Secure access to your Debt Collector Kill Switch purchase.",
    canonical: "https://debtcollectorkillswitch.com/thank-you",
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState(token ? "checking" : "missing"); // checking | valid | invalid | missing
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    if (!token) return;
    const verify = async () => {
      try {
        const res = await fetch(`${API}/api/purchases/verify?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        setStatus(data.valid ? "valid" : "invalid");
      } catch (err) {
        setStatus("invalid");
      }
    };
    verify();
  }, [token]);

  const handleDownload = async () => {
    setDownloadError("");
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/download?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setDownloadError(data.detail || "Download temporarily unavailable. Please try again shortly or contact support.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "debt-collector-kill-switch.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError("Download temporarily unavailable. Please try again shortly or contact support.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-ivory text-brand-dark font-sans" data-testid="thank-you-page">
      {/* Compact Header */}
      <header className="bg-brand-ivory border-b border-brand-border py-4 px-4 md:px-6" data-testid="thank-you-page-header">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
          <span className="font-display font-black uppercase text-sm md:text-base text-brand-dark tracking-wide">
            DEBT COLLECTOR KILL SWITCH
          </span>
        </div>
      </header>

      <main className="max-w-[720px] mx-auto px-4 md:px-6 py-10 md:py-16">
        {/* Congratulations */}
        <p
          className="font-display font-black uppercase text-2xl sm:text-3xl mb-4 text-center"
          data-testid="thank-you-congrats"
        >
          <span className="text-brand-dark bg-brand-yellow px-2">CONGRATULATIONS!</span>
        </p>

        {/* Main Headline */}
        <h1
          className="font-display font-black uppercase text-brand-dark tracking-tight leading-tight text-center mb-6 w-full"
          data-testid="thank-you-headline"
        >
          <span className="block whitespace-nowrap text-[clamp(0.5rem,2.55vw,1.2rem)]">
            YOU JUST TOOK THE FIRST STEP TOWARDS STOPPING DEBT COLLECTORS
          </span>
          <span className="block whitespace-nowrap text-[clamp(0.5rem,2.4vw,1.15rem)] mt-1">
            AND REMOVING NEGATIVE COLLECTION ACCOUNTS ON YOUR CREDIT REPORT!
          </span>
        </h1>

        {/* Supporting Text */}
        <div className="text-center mb-8">
          <p className="font-sans text-brand-dark/80 text-sm sm:text-base" data-testid="thank-you-card-statement">
            YOUR CARD STATEMENT WILL SHOW: <span className="bg-brand-yellow text-black font-bold px-1">TDCR CONSULTING, LLC</span>
          </p>
          <p className="font-sans font-bold uppercase text-brand-dark text-base sm:text-lg mt-2" data-testid="thank-you-supporting-text">
            YOUR DEBT COLLECTOR KILL SWITCH IS READY!
          </p>
          <p className="font-sans text-brand-dark/70 text-sm sm:text-base mt-1">
            Your secure product access is provided below:
          </p>
        </div>

        {/* Secure Access Area */}
        <div
          className="bg-card border border-brand-border rounded-xl shadow-sm p-6 sm:p-8 text-center mb-8"
          data-testid="thank-you-access-area"
        >
          {status === "checking" && (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="thank-you-checking-state">
              <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
              <p className="font-sans font-bold text-brand-dark text-sm sm:text-base">VERIFYING YOUR PURCHASE...</p>
            </div>
          )}

          {status === "valid" && (
            <>
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="thank-you-download-button"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-4 px-8 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-sm sm:text-base tracking-wide transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                {downloading ? "PREPARING YOUR DOWNLOAD..." : "DOWNLOAD YOUR DEBT COLLECTOR KILL SWITCH"}
              </button>
              <p className="font-sans text-brand-dark/60 text-xs sm:text-sm mt-3">
                Keep a copy of your files in a safe location for future reference.
              </p>
              {downloadError && (
                <p className="text-brand-red text-sm font-semibold mt-4" data-testid="thank-you-download-error">
                  {downloadError}
                </p>
              )}
            </>
          )}

          {(status === "invalid" || status === "missing") && (
            <div data-testid="thank-you-invalid-state">
              <XCircle className="w-10 h-10 text-brand-red mx-auto mb-3" />
              <p className="font-sans font-bold text-brand-dark text-sm sm:text-base mb-2">
                WE COULDN&rsquo;T VERIFY A VALID PURCHASE FOR THIS LINK.
              </p>
              <p className="font-sans text-brand-dark/70 text-sm">
                If you just completed a purchase, please contact us below and we will get your access restored.
              </p>
            </div>
          )}
        </div>

        {/* Delivery & Support Promise */}
        <p className="text-center font-sans text-brand-dark/70 text-sm sm:text-base mb-10 leading-relaxed" data-testid="thank-you-support-promise">
          <span className="font-bold text-brand-dark">DIGITAL DELIVERY &amp; SUPPORT PROMISE</span>
          <br />
          If you experience an access or delivery problem, contact us and we will work to restore or re-deliver your Kill Switch files.
        </p>

        {/* Need Help */}
        <div className="text-center border-t border-brand-border pt-8" data-testid="thank-you-help-section">
          <p className="font-display font-black uppercase text-brand-dark text-sm sm:text-base mb-2">
            NEED HELP WITH YOUR PURCHASE?
          </p>
          <a
            href="mailto:info@debtcollectorkillswitch.com"
            data-testid="thank-you-support-email"
            className="font-sans font-bold text-brand-red hover:text-brand-cta-hover transition-colors text-sm sm:text-base"
          >
            info@debtcollectorkillswitch.com
          </a>
          <p className="font-sans text-brand-dark/60 text-xs sm:text-sm mt-2">
            Hours of Operation:
            <br />
            Mon &ndash; Fri: 9am &ndash; 5:00pm PST
          </p>
          <Link
            to="/contact"
            data-testid="thank-you-contact-faq-link"
            className="inline-block mt-4 font-sans font-bold text-xs sm:text-sm text-brand-dark/70 hover:text-brand-red transition-colors"
          >
            CONTACT US / FAQ
          </Link>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
